import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { useFichesATraiter } from '../../lib/useEspaceFormateur';

/** Nombre d'actions détaillées dans le bandeau ; la liste complète est dans l'écran. */
const ACTIONS_AFFICHEES = 3;

function cleDuJour(): string {
  const jour = new Date().toLocaleDateString('sv-SE', { timeZone: 'Indian/Reunion' });
  return `tf:rappel-fiches:${jour}`;
}

function dejaFerme(cle: string): boolean {
  try {
    return window.localStorage.getItem(cle) === '1';
  } catch {
    return false;
  }
}

/**
 * Bandeau de rappel des actions du jour (CDC §7.3), sur tous les écrans du
 * formateur. C'est l'API qui décide de l'afficher — à partir de 15 h 30, heure
 * de La Réunion, quand la règle d'alerte est active sur son antenne et qu'une
 * action l'attend — ; le formateur peut le fermer pour la journée. L'écran des
 * fiches porte déjà ces actions : le bandeau ne s'y répète pas.
 */
export function BandeauRappelActions(): JSX.Element | null {
  const { utilisateur } = useAuth();
  const { pathname } = useLocation();
  const estFormateur = utilisateur?.profil === 'FORMATEUR';
  const { data } = useFichesATraiter(estFormateur);
  const cle = cleDuJour();
  const [fermeLe, setFermeLe] = useState<string | null>(() => (dejaFerme(cle) ? cle : null));

  const rappel = data?.rappel;
  if (!estFormateur || !rappel?.afficher || fermeLe === cle || pathname.startsWith('/form-fiches')) {
    return null;
  }

  const fermer = (): void => {
    try {
      window.localStorage.setItem(cle, '1');
    } catch {
      // Stockage indisponible : le bandeau reste fermé jusqu'au rechargement.
    }
    setFermeLe(cle);
  };

  const bloque = rappel.bloque;
  const reste = rappel.actions.length - ACTIONS_AFFICHEES;

  return (
    <div className="mx-auto max-w-[1500px] px-4 pt-4 sm:px-6 sm:pt-6">
      <div
        role="status"
        className={`flex flex-col items-start gap-3 rounded-2xl border px-4 py-4 sm:flex-row sm:px-5 ${
          bloque ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
        }`}
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
            bloque ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${bloque ? 'text-red-900' : 'text-amber-900'}`}>
            Rappel des actions du jour · {rappel.heure.replace(':', 'h')}
          </div>
          <ul className={`mt-1 space-y-0.5 text-xs ${bloque ? 'text-red-800' : 'text-amber-800'}`}>
            {rappel.actions.slice(0, ACTIONS_AFFICHEES).map((action) => (
              <li key={action.seanceId} className="break-words">
                <span className="font-semibold">{action.libelle}</span> :{' '}
                {action.horsDelai && 'hors délai, vos autres fiches sont bloquées. '}
                {action.etape}
              </li>
            ))}
            {reste > 0 && <li>Et {reste} autre{reste > 1 ? 's' : ''} action{reste > 1 ? 's' : ''}.</li>}
          </ul>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Link
            to="/form-fiches"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl bg-codialis-mint px-4 text-sm font-semibold text-codialis-navy transition hover:brightness-95 sm:flex-none"
          >
            Mes fiches séquence
          </Link>
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer le rappel pour aujourd’hui"
            className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-xl text-codialis-gray transition hover:bg-white/60 hover:text-codialis-navy"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
